import { useEffect, useState, useMemo } from 'react';
import type { DepthAnalysis, DepthHistoryPoint } from '../types/trade';
import { api } from '../services/api';

interface LiquidityDepthProps {
  symbol?: string;
  refreshInterval?: number;
}

const LEVEL_KEYS = ['1', '2', '5', '10'] as const;

function formatUsd(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

function formatPrice(v: number): string {
  if (v >= 1) return `$${v.toFixed(4)}`;
  if (v >= 0.01) return `$${v.toFixed(5)}`;
  return `$${v.toFixed(6)}`;
}

// --- SVG Depth Chart ---

interface DepthChartProps {
  bids: { price: number; cumulative: number }[];
  asks: { price: number; cumulative: number }[];
  midPrice: number;
  width?: number;
  height?: number;
}

function DepthChart({ bids, asks, midPrice, width = 800, height = 360 }: DepthChartProps) {
  const padding = { top: 20, right: 60, bottom: 40, left: 60 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const { minPrice, maxPrice, maxCumulative, bidPath, askPath, bidFill, askFill, yTicks, xTicks } =
    useMemo(() => {
      if (!bids.length && !asks.length) {
        return {
          minPrice: 0,
          maxPrice: 1,
          maxCumulative: 1,
          bidPath: '',
          askPath: '',
          bidFill: '',
          askFill: '',
          yTicks: [] as number[],
          xTicks: [] as number[],
        };
      }

      const allBidPrices = bids.map((b) => b.price);
      const allAskPrices = asks.map((a) => a.price);
      const minP = allBidPrices.length ? Math.min(...allBidPrices) : midPrice * 0.9;
      const maxP = allAskPrices.length ? Math.max(...allAskPrices) : midPrice * 1.1;

      const range = maxP - minP;
      const adjMin = minP - range * 0.02;
      const adjMax = maxP + range * 0.02;

      const maxC = Math.max(
        bids.length ? bids[bids.length - 1].cumulative : 0,
        asks.length ? asks[asks.length - 1].cumulative : 0,
        1
      );

      const scaleX = (p: number) => ((p - adjMin) / (adjMax - adjMin)) * chartW;
      const scaleY = (c: number) => chartH - (c / maxC) * chartH;

      // Bids: descending price, draw left to right (lowest first)
      const sortedBids = [...bids].reverse();
      let bp = '';
      let bf = `M ${scaleX(sortedBids.length ? sortedBids[0].price : midPrice)} ${chartH}`;
      sortedBids.forEach((pt, i) => {
        const x = scaleX(pt.price);
        const y = scaleY(pt.cumulative);
        if (i === 0) {
          bp = `M ${x} ${y}`;
          bf += ` L ${x} ${y}`;
        } else {
          // Step: horizontal then vertical
          const prevY = scaleY(sortedBids[i - 1].cumulative);
          bp += ` L ${x} ${prevY} L ${x} ${y}`;
          bf += ` L ${x} ${prevY} L ${x} ${y}`;
        }
      });
      if (sortedBids.length) {
        const lastX = scaleX(sortedBids[sortedBids.length - 1].price);
        bf += ` L ${lastX} ${chartH} Z`;
      }

      // Asks: ascending price, draw left to right
      let ap = '';
      let af = '';
      if (asks.length) {
        af = `M ${scaleX(asks[0].price)} ${chartH}`;
        asks.forEach((pt, i) => {
          const x = scaleX(pt.price);
          const y = scaleY(pt.cumulative);
          if (i === 0) {
            ap = `M ${x} ${y}`;
            af += ` L ${x} ${y}`;
          } else {
            const prevY = scaleY(asks[i - 1].cumulative);
            ap += ` L ${x} ${prevY} L ${x} ${y}`;
            af += ` L ${x} ${prevY} L ${x} ${y}`;
          }
        });
        const lastX = scaleX(asks[asks.length - 1].price);
        af += ` L ${lastX} ${chartH} Z`;
      }

      // Y ticks
      const ySteps = 5;
      const yt: number[] = [];
      for (let i = 0; i <= ySteps; i++) {
        yt.push((maxC / ySteps) * i);
      }

      // X ticks (price)
      const xSteps = 6;
      const xt: number[] = [];
      for (let i = 0; i <= xSteps; i++) {
        xt.push(adjMin + ((adjMax - adjMin) / xSteps) * i);
      }

      return {
        minPrice: adjMin,
        maxPrice: adjMax,
        maxCumulative: maxC,
        bidPath: bp,
        askPath: ap,
        bidFill: bf,
        askFill: af,
        yTicks: yt,
        xTicks: xt,
      };
    }, [bids, asks, midPrice, chartW, chartH]);

  const scaleX = (p: number) => padding.left + ((p - minPrice) / (maxPrice - minPrice)) * chartW;
  const scaleY = (c: number) => padding.top + chartH - (c / maxCumulative) * chartH;
  const midX = scaleX(midPrice);

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="depth-chart-svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Grid lines */}
      {yTicks.map((t, i) => {
        const y = scaleY(t);
        return (
          <g key={`yg-${i}`}>
            <line
              x1={padding.left}
              y1={y}
              x2={width - padding.right}
              y2={y}
              stroke="#30363d"
              strokeWidth="1"
            />
            <text
              x={padding.left - 8}
              y={y + 4}
              textAnchor="end"
              fill="#8b949e"
              fontSize="10"
            >
              {formatUsd(t)}
            </text>
          </g>
        );
      })}

      {/* X axis labels */}
      {xTicks.map((t, i) => {
        const x = scaleX(t);
        return (
          <text
            key={`xt-${i}`}
            x={x}
            y={height - 8}
            textAnchor="middle"
            fill="#8b949e"
            fontSize="10"
          >
            {formatPrice(t)}
          </text>
        );
      })}

      {/* Bid area */}
      <path
        d={bidFill}
        fill="rgba(38, 166, 154, 0.15)"
        transform={`translate(${padding.left}, ${padding.top})`}
      />
      <path
        d={bidPath}
        fill="none"
        stroke="#26a69a"
        strokeWidth="2"
        transform={`translate(${padding.left}, ${padding.top})`}
      />

      {/* Ask area */}
      <path
        d={askFill}
        fill="rgba(239, 83, 80, 0.15)"
        transform={`translate(${padding.left}, ${padding.top})`}
      />
      <path
        d={askPath}
        fill="none"
        stroke="#ef5350"
        strokeWidth="2"
        transform={`translate(${padding.left}, ${padding.top})`}
      />

      {/* Mid price line */}
      <line
        x1={midX}
        y1={padding.top}
        x2={midX}
        y2={padding.top + chartH}
        stroke="#58a6ff"
        strokeWidth="1"
        strokeDasharray="4 3"
      />
      <text
        x={midX}
        y={padding.top - 6}
        textAnchor="middle"
        fill="#58a6ff"
        fontSize="10"
        fontWeight="600"
      >
        Mid {formatPrice(midPrice)}
      </text>

      {/* Axis labels */}
      <text
        x={width / 2}
        y={height - 0}
        textAnchor="middle"
        fill="#8b949e"
        fontSize="11"
      >
        Price (USDT)
      </text>
      <text
        x={12}
        y={height / 2}
        textAnchor="middle"
        fill="#8b949e"
        fontSize="11"
        transform={`rotate(-90, 12, ${height / 2})`}
      >
        Cumulative Depth (USD)
      </text>
    </svg>
  );
}

// --- Depth History Mini Chart ---

interface DepthHistoryChartProps {
  data: DepthHistoryPoint[];
  width?: number;
  height?: number;
}

function DepthHistoryChart({ data, width = 800, height = 140 }: DepthHistoryChartProps) {
  const padding = { top: 10, right: 50, bottom: 28, left: 60 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  const { path, fill, yTicks, xLabels, maxVal } = useMemo(() => {
    if (!data.length)
      return { path: '', fill: '', yTicks: [] as number[], xLabels: [] as { x: number; label: string }[], maxVal: 1 };

    const vals = data.map((d) => d.depth_2pct);
    const mv = Math.max(...vals, 1);

    const scaleX = (i: number) => (i / Math.max(data.length - 1, 1)) * chartW;
    const scaleY = (v: number) => chartH - (v / mv) * chartH;

    let p = '';
    let f = `M 0 ${chartH}`;
    data.forEach((d, i) => {
      const x = scaleX(i);
      const y = scaleY(d.depth_2pct);
      if (i === 0) {
        p = `M ${x} ${y}`;
        f += ` L ${x} ${y}`;
      } else {
        p += ` L ${x} ${y}`;
        f += ` L ${x} ${y}`;
      }
    });
    f += ` L ${scaleX(data.length - 1)} ${chartH} Z`;

    const yt = [0, mv * 0.5, mv];

    // X labels: show ~4 time labels
    const step = Math.max(Math.floor(data.length / 4), 1);
    const xl: { x: number; label: string }[] = [];
    for (let i = 0; i < data.length; i += step) {
      const d = new Date(data[i].time);
      xl.push({
        x: scaleX(i),
        label: `${d.getUTCHours().toString().padStart(2, '0')}:${d.getUTCMinutes().toString().padStart(2, '0')}`,
      });
    }

    return { path: p, fill: f, yTicks: yt, xLabels: xl, maxVal: mv };
  }, [data, chartW, chartH]);

  if (!data.length) {
    return (
      <div className="depth-history-empty">
        No depth history available
      </div>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className="depth-history-svg"
      preserveAspectRatio="xMidYMid meet"
    >
      {yTicks.map((t, i) => {
        const y = padding.top + chartH - (t / maxVal) * chartH;
        return (
          <g key={`yh-${i}`}>
            <line
              x1={padding.left}
              y1={y}
              x2={width - padding.right}
              y2={y}
              stroke="#30363d"
              strokeWidth="1"
            />
            <text x={padding.left - 6} y={y + 4} textAnchor="end" fill="#8b949e" fontSize="9">
              {formatUsd(t)}
            </text>
          </g>
        );
      })}
      {xLabels.map((xl, i) => (
        <text
          key={`xh-${i}`}
          x={padding.left + xl.x}
          y={height - 4}
          textAnchor="middle"
          fill="#8b949e"
          fontSize="9"
        >
          {xl.label}
        </text>
      ))}
      <path
        d={fill}
        fill="rgba(88, 166, 255, 0.12)"
        transform={`translate(${padding.left}, ${padding.top})`}
      />
      <path
        d={path}
        fill="none"
        stroke="#58a6ff"
        strokeWidth="1.5"
        transform={`translate(${padding.left}, ${padding.top})`}
      />
    </svg>
  );
}

// --- Main Component ---

export function LiquidityDepth({
  symbol = 'fco_usdt',
  refreshInterval = 5000,
}: LiquidityDepthProps) {
  const [depth, setDepth] = useState<DepthAnalysis | null>(null);
  const [history, setHistory] = useState<DepthHistoryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historyHours, setHistoryHours] = useState(24);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
  }, [symbol, refreshInterval]);

  useEffect(() => {
    loadHistory();
  }, [symbol, historyHours]);

  const loadData = async () => {
    try {
      const data = await api.getDepthAnalysis(symbol);
      setDepth(data);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load depth data');
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const data = await api.getDepthHistory(symbol, historyHours);
      setHistory(data);
    } catch {
      // History is supplementary, don't block the main view
    }
  };

  if (loading && !depth) {
    return <div className="depth-container depth-loading">Loading depth data...</div>;
  }

  if (error && !depth) {
    return <div className="depth-container depth-error">{error}</div>;
  }

  if (!depth) return null;

  const maxDepth = Math.max(
    ...LEVEL_KEYS.map((k) => {
      const l = depth.levels[k];
      return l ? Math.max(l.bid, l.ask) : 0;
    }),
    1
  );

  return (
    <div className="depth-container">
      {/* Depth Levels Panel */}
      <div className="depth-panel depth-levels-panel">
        <h3 className="depth-panel-title">Depth Levels (from mid price)</h3>
        <div className="depth-levels-grid">
          <div className="depth-levels-header">
            <span className="dl-label-col"></span>
            <span className="dl-bid-header">Bid Depth</span>
            <span className="dl-ask-header">Ask Depth</span>
          </div>
          {LEVEL_KEYS.map((key) => {
            const level = depth.levels[key];
            if (!level) return null;
            const bidPct = (level.bid / maxDepth) * 100;
            const askPct = (level.ask / maxDepth) * 100;
            return (
              <div key={key} className="depth-level-row">
                <span className="dl-pct-label">{key}%</span>
                <div className="dl-bar-cell dl-bid-bar-cell">
                  <div className="dl-bar-track">
                    <div
                      className="dl-bar dl-bar-bid"
                      style={{ width: `${bidPct}%` }}
                    />
                  </div>
                  <span className="dl-value dl-bid-value">{formatUsd(level.bid)}</span>
                </div>
                <div className="dl-bar-cell dl-ask-bar-cell">
                  <div className="dl-bar-track">
                    <div
                      className="dl-bar dl-bar-ask"
                      style={{ width: `${askPct}%` }}
                    />
                  </div>
                  <span className="dl-value dl-ask-value">{formatUsd(level.ask)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Depth Chart */}
      <div className="depth-panel depth-chart-panel">
        <h3 className="depth-panel-title">Order Book Depth</h3>
        <DepthChart
          bids={depth.depth_curve.bids}
          asks={depth.depth_curve.asks}
          midPrice={depth.mid_price}
        />
      </div>

      {/* Bottom Row: Metrics + OM Comparison */}
      <div className="depth-bottom-row">
        {/* Metrics Cards */}
        <div className="depth-panel depth-metrics-panel">
          <h3 className="depth-panel-title">Key Metrics</h3>
          <div className="depth-metrics-grid">
            <div className="depth-metric-card">
              <span className="dmc-label">Mid Price</span>
              <span className="dmc-value">{formatPrice(depth.mid_price)}</span>
            </div>
            <div className="depth-metric-card">
              <span className="dmc-label">Spread</span>
              <span className={`dmc-value ${depth.spread_pct <= 1 ? 'dmc-ok' : 'dmc-warn'}`}>
                {depth.spread_pct.toFixed(2)}%
              </span>
            </div>
            <div className="depth-metric-card">
              <span className="dmc-label">Depth at 2%</span>
              <span className="dmc-value">
                {formatUsd((depth.levels['2']?.bid || 0) + (depth.levels['2']?.ask || 0))}
              </span>
            </div>
            <div className="depth-metric-card">
              <span className="dmc-label">Bid/Ask Imbalance</span>
              <span className={`dmc-value ${depth.imbalance > 1.2 ? 'dmc-bid-heavy' : depth.imbalance < 0.8 ? 'dmc-ask-heavy' : ''}`}>
                {depth.imbalance.toFixed(2)}x
              </span>
            </div>
            <div className="depth-metric-card">
              <span className="dmc-label">Bid 2% Depth</span>
              <span className="dmc-value dmc-bid">{formatUsd(depth.levels['2']?.bid || 0)}</span>
            </div>
            <div className="depth-metric-card">
              <span className="dmc-label">Ask 2% Depth</span>
              <span className="dmc-value dmc-ask">{formatUsd(depth.levels['2']?.ask || 0)}</span>
            </div>
          </div>
        </div>

        {/* OM Comparison */}
        <div className="depth-panel depth-om-panel">
          <h3 className="depth-panel-title">OM/USDT Comparison (Binance)</h3>
          {depth.om_comparison ? (
            <div className="om-comparison-grid">
              <div className="om-row om-header-row">
                <span className="om-metric-col"></span>
                <span className="om-fco-col">FCO</span>
                <span className="om-om-col">OM</span>
              </div>
              <div className="om-row">
                <span className="om-metric-col">Mid Price</span>
                <span className="om-fco-col">{formatPrice(depth.mid_price)}</span>
                <span className="om-om-col">{formatPrice(depth.om_comparison.mid_price)}</span>
              </div>
              <div className="om-row">
                <span className="om-metric-col">Spread</span>
                <span className="om-fco-col">{depth.spread_pct.toFixed(2)}%</span>
                <span className="om-om-col">{depth.om_comparison.spread_pct.toFixed(2)}%</span>
              </div>
              <div className="om-row">
                <span className="om-metric-col">Depth 1%</span>
                <span className="om-fco-col">{formatUsd((depth.levels['1']?.bid || 0) + (depth.levels['1']?.ask || 0))}</span>
                <span className="om-om-col">{formatUsd((depth.om_comparison.levels['1']?.bid || 0) + (depth.om_comparison.levels['1']?.ask || 0))}</span>
              </div>
              <div className="om-row">
                <span className="om-metric-col">Depth 2%</span>
                <span className="om-fco-col">{formatUsd((depth.levels['2']?.bid || 0) + (depth.levels['2']?.ask || 0))}</span>
                <span className="om-om-col">{formatUsd((depth.om_comparison.levels['2']?.bid || 0) + (depth.om_comparison.levels['2']?.ask || 0))}</span>
              </div>
              <div className="om-row">
                <span className="om-metric-col">Depth 5%</span>
                <span className="om-fco-col">{formatUsd((depth.levels['5']?.bid || 0) + (depth.levels['5']?.ask || 0))}</span>
                <span className="om-om-col">{formatUsd((depth.om_comparison.levels['5']?.bid || 0) + (depth.om_comparison.levels['5']?.ask || 0))}</span>
              </div>
              <div className="om-timestamp">
                Updated: {new Date(depth.om_comparison.timestamp).toLocaleTimeString()}
              </div>
            </div>
          ) : (
            <div className="om-unavailable">OM comparison data not available</div>
          )}
        </div>
      </div>

      {/* Depth History */}
      <div className="depth-panel depth-history-panel">
        <div className="depth-history-header">
          <h3 className="depth-panel-title">Depth History (2%)</h3>
          <div className="depth-history-controls">
            {[6, 12, 24, 48, 168].map((h) => (
              <button
                key={h}
                className={`dh-btn ${historyHours === h ? 'dh-btn-active' : ''}`}
                onClick={() => setHistoryHours(h)}
              >
                {h <= 24 ? `${h}h` : `${h / 24}d`}
              </button>
            ))}
          </div>
        </div>
        <DepthHistoryChart data={history} />
      </div>
    </div>
  );
}
